import React, {Component} from 'react';
import {Link, Redirect} from "react-router-dom";
import moment from 'moment';
import {deletePage, duplicatePage, listPages, getPage, updatePage} from '../services/pageService';
import {listFolders, movePageToFolder} from '../services/folderService';
import config from '../config/config';
import {Helmet} from "react-helmet";
import pagesStyles from './Pages.module.css';
import {DataListCard, DataListToolbar, DataListHeaderRow, DataListSkeleton} from '../patterns/datalist/DataList';
import DataRow from '../patterns/datalist/DataRow';
import BulkBar from '../patterns/datalist/BulkBar';
import ConfirmDialog from '../patterns/datalist/ConfirmDialog';
import RenameDialog from '../patterns/datalist/RenameDialog';
import Toast from '../patterns/Toast';
import EmptyState from '../patterns/EmptyState';
import {FirstUseIllustration, NoResultsIllustration, ErrorIllustration} from '../patterns/EmptyStateIllustrations';
import MovePage from '../components/pages/move';
import ErrorAlert from '../components/ErrorAlert';

const PlusIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" aria-hidden="true" {...props}>
        <path d="M12 5v14M5 12h14"/>
    </svg>
);
const OpenIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M14 4h6v6M20 4l-9 9M18 14v5H5V6h5"/>
    </svg>
);
const RenameIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M4 20h4L19 9l-4-4L4 16z"/>
    </svg>
);
const DuplicateIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <rect x="8" y="8" width="12" height="12" rx="2"/>
        <path d="M4 16V4h12"/>
    </svg>
);
const DownloadIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M12 4v12M7 11l5 5 5-5M4 20h16"/>
    </svg>
);
const MoveIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-8l-2-2H5a2 2 0 0 0-2 2z"/>
    </svg>
);
const TrashIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"/>
    </svg>
);

const formatUpdated = (dateString) => {
    if (!dateString) return '';
    const days = moment().diff(moment(dateString), 'days');
    return days >= 7 ? moment(dateString).format('D MMM') : moment(dateString).fromNow();
};

export default class Pages extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pages: null,
            loadError: null,
            folders: [],
            search: '',
            sortDirection: 'desc',
            selectedIds: [],
            openMenuId: null,
            pageToDelete: null,
            pagesToBulkDelete: null,
            deleteBusy: false,
            pageToRename: null,
            renameBusy: false,
            pageToMove: null,
            bulkMove: false,
            destFolderId: null,
            movePageErrorMessage: null,
            toast: null,
            redirectTo: null,
            temporaryErrorMessage: null,
        }
    }

    async componentDidMount() {
        await this.loadPages();
    }

    loadPages = async () => {
        try {
            const pages = await listPages();
            this.setState({pages, loadError: null});
        } catch (err) {
            this.setState({loadError: err.toString(), pages: []});
        }
    }

    visiblePages = () => {
        const {pages, search, sortDirection} = this.state;
        if (!pages) return [];
        const q = search.trim().toLowerCase();
        const filtered = q ? pages.filter((p) => p.title?.toLowerCase().includes(q)) : pages;
        return [...filtered].sort((a, b) => {
            const diff = new Date(b.modifiedAt || 0) - new Date(a.modifiedAt || 0);
            return sortDirection === 'desc' ? diff : -diff;
        });
    }

    toggleSelect = (id, selected) => {
        this.setState((s) => ({
            selectedIds: selected ? [...s.selectedIds, id] : s.selectedIds.filter((i) => i !== id),
        }));
    }

    toggleSelectAll = (selected) => {
        this.setState({selectedIds: selected ? this.visiblePages().map((p) => p.id) : []});
    }

    clearSelection = () => this.setState({selectedIds: []});

    menuItemsFor = (page, showFolders) => {
        const items = [
            {key: 'open', label: 'Open', icon: <OpenIcon/>, onSelect: () => this.setState({redirectTo: `/pages/${page.id}`})},
            {key: 'rename', label: 'Rename', icon: <RenameIcon/>, onSelect: () => this.setState({pageToRename: page})},
            {key: 'duplicate', label: 'Duplicate', icon: <DuplicateIcon/>, onSelect: () => this.performDuplicate(page)},
            {key: 'download', label: 'Download', icon: <DownloadIcon/>, onSelect: () => this.performDownload(page)},
        ];
        if (showFolders) {
            items.push({key: 'move', label: 'Move…', icon: <MoveIcon/>, onSelect: () => this.openMove(page)});
        }
        items.push({key: 'divider', divider: true});
        items.push({key: 'delete', label: 'Delete…', icon: <TrashIcon/>, danger: true, onSelect: () => this.setState({pageToDelete: page})});
        return items.filter((i) => !i.divider);
    }

    performDuplicate = async (page) => {
        try {
            const newId = await duplicatePage(page.id);
            await this.loadPages();
            this.setState({
                toast: {
                    message: 'Page duplicated',
                    actionLabel: 'Undo',
                    onAction: async () => {
                        try {
                            await deletePage(newId);
                            await this.loadPages();
                        } catch (err) {
                            this.setState({temporaryErrorMessage: `Unable to undo duplicate: ${err.toString()}`});
                        }
                        this.setState({toast: null});
                    },
                },
            });
        } catch (err) {
            this.setState({temporaryErrorMessage: `Error duplicating page '${page.title}': ${err.toString()}`});
        }
    }

    performDownload = (page) => {
        window.open(`${config.liveLink.baseUrl}/${page.id}.png`, '_blank', 'noopener,noreferrer');
    }

    confirmDelete = async () => {
        const {pageToDelete, pagesToBulkDelete} = this.state;
        this.setState({deleteBusy: true});
        try {
            if (pagesToBulkDelete) {
                await Promise.all(pagesToBulkDelete.map((p) => deletePage(p.id)));
                this.setState({selectedIds: [], toast: {message: `${pagesToBulkDelete.length} pages deleted`}});
            } else {
                await deletePage(pageToDelete.id);
                this.setState({toast: {message: `'${pageToDelete.title}' deleted`}});
            }
            await this.loadPages();
        } catch (err) {
            this.setState({temporaryErrorMessage: `Error deleting: ${err.toString()}`});
        }
        this.setState({pageToDelete: null, pagesToBulkDelete: null, deleteBusy: false});
    }

    cancelDelete = () => this.setState({pageToDelete: null, pagesToBulkDelete: null});

    performRename = async (title) => {
        // Rename reuses the same update flow the editor's Page details panel
        // uses - see updatePage in pageService.
        this.setState({renameBusy: true});
        try {
            const full = await getPage(this.state.pageToRename.id);
            await updatePage(this.state.pageToRename.id, JSON.stringify({
                title,
                backgroundImage: null,
                tasks: full.tasks,
                notes: full.notes,
            }), full.backgroundImage);
            await this.loadPages();
        } catch (err) {
            this.setState({temporaryErrorMessage: `Error renaming page: ${err.toString()}`});
        }
        this.setState({pageToRename: null, renameBusy: false});
    }

    openMove = async (page) => {
        try {
            const folders = await listFolders();
            this.setState({pageToMove: page, bulkMove: false, folders, destFolderId: page.folderId});
        } catch (err) {
            this.setState({temporaryErrorMessage: `Unable to load folders: ${err.toString()}`});
        }
    }

    openBulkMove = async () => {
        try {
            const folders = await listFolders();
            this.setState({pageToMove: {}, bulkMove: true, folders, destFolderId: null});
        } catch (err) {
            this.setState({temporaryErrorMessage: `Unable to load folders: ${err.toString()}`});
        }
    }

    cancelMove = () => this.setState({pageToMove: null, destFolderId: null, movePageErrorMessage: null});

    performMove = async () => {
        const {pageToMove, bulkMove, destFolderId, selectedIds, pages} = this.state;
        try {
            if (bulkMove) {
                await Promise.all(selectedIds.map((id) => movePageToFolder(id, destFolderId)));
                this.setState({selectedIds: [], toast: {message: `${selectedIds.length} pages moved`}});
            } else {
                const previousFolderId = pageToMove.folderId;
                await movePageToFolder(pageToMove.id, destFolderId);
                this.setState({
                    toast: {
                        message: `'${pageToMove.title}' moved`,
                        actionLabel: 'Undo',
                        onAction: async () => {
                            try {
                                await movePageToFolder(pageToMove.id, previousFolderId);
                                await this.loadPages();
                            } catch (err) {
                                this.setState({temporaryErrorMessage: `Unable to undo move: ${err.toString()}`});
                            }
                            this.setState({toast: null});
                        },
                    },
                });
            }
            await this.loadPages();
        } catch (err) {
            this.setState({movePageErrorMessage: `Error moving page: ${err.toString()}`});
            return;
        }
        this.setState({pageToMove: null, destFolderId: null, pages});
    }

    clearTemporaryErrorMessage = () => this.setState({temporaryErrorMessage: null});

    render() {
        if (!this.props.authentication) {
            return <Redirect to='/'/>
        }
        if (this.state.redirectTo) {
            return <Redirect to={this.state.redirectTo}/>
        }

        const showFolders = this.props.authentication?.organisation?.status === 'PAID';
        const loading = this.state.pages === null;
        const visible = this.visiblePages();
        const {selectedIds} = this.state;
        const selectedPages = (this.state.pages || []).filter((p) => selectedIds.includes(p.id));

        return (
            <div className={pagesStyles.root}>
                <Helmet><title>Ovalview | My pages</title></Helmet>
                <div className={pagesStyles.page}>
                    <ErrorAlert message={this.state.temporaryErrorMessage} onClose={this.clearTemporaryErrorMessage} delay={3000}/>

                    <div className={pagesStyles.header}>
                        <h1 className={pagesStyles.title}>My pages</h1>
                    </div>

                    <DataListCard>
                        {!loading && !this.state.loadError && this.state.pages.length > 0 && (
                            <>
                                <DataListToolbar
                                    searchValue={this.state.search}
                                    onSearchChange={(v) => this.setState({search: v})}
                                    searchPlaceholder="Search pages"
                                    primaryAction={
                                        <Link to="/pages/new" className={pagesStyles.newPageButton}>
                                            <PlusIcon/>New page
                                        </Link>
                                    }
                                />
                                <DataListHeaderRow
                                    allSelected={visible.length > 0 && visible.every((p) => selectedIds.includes(p.id))}
                                    onSelectAllChange={this.toggleSelectAll}
                                    sortDirection={this.state.sortDirection}
                                    onSortChange={() => this.setState((s) => ({sortDirection: s.sortDirection === 'desc' ? 'asc' : 'desc'}))}
                                />
                            </>
                        )}

                        {loading && <DataListSkeleton/>}

                        {!loading && this.state.loadError && (
                            <EmptyState
                                tone="error"
                                illustration={<ErrorIllustration/>}
                                title="We couldn't load your pages"
                                description="Check your connection and try again. Nothing you've saved has been lost."
                                primaryAction={
                                    <button type="button" className={pagesStyles.retryButton} onClick={this.loadPages}>
                                        Try again
                                    </button>
                                }
                            />
                        )}

                        {!loading && !this.state.loadError && this.state.pages.length === 0 && (
                            <EmptyState
                                large
                                illustration={<FirstUseIllustration/>}
                                title="Create your first page"
                                description="Upload a diagram of the feature you're planning, then draw an oval around each task."
                                primaryAction={
                                    <Link to="/pages/new" className={pagesStyles.newPageButton}>
                                        <PlusIcon/>Create a page
                                    </Link>
                                }
                                secondaryAction={<Link to="/HowTo" className={pagesStyles.howLink}>How pages work</Link>}
                            />
                        )}

                        {!loading && !this.state.loadError && this.state.pages.length > 0 && visible.length === 0 && (
                            <EmptyState
                                illustration={<NoResultsIllustration/>}
                                title={`No pages match "${this.state.search}"`}
                                description="Check the spelling or try a shorter search."
                                primaryAction={
                                    <button type="button" className={pagesStyles.clearSearchButton} onClick={() => this.setState({search: ''})}>
                                        Clear search
                                    </button>
                                }
                            />
                        )}

                        {!loading && visible.map((page) => (
                            <DataRow
                                key={page.id}
                                id={page.id}
                                name={page.title}
                                href={`/pages/${page.id}`}
                                folder={page.folderName}
                                updated={formatUpdated(page.modifiedAt)}
                                selected={selectedIds.includes(page.id)}
                                onSelectChange={(sel) => this.toggleSelect(page.id, sel)}
                                menuOpen={this.state.openMenuId === page.id}
                                onMenuOpenChange={(open) => this.setState({openMenuId: open ? page.id : null})}
                                menuItems={this.menuItemsFor(page, showFolders)}
                            />
                        ))}
                    </DataListCard>
                </div>

                <BulkBar
                    count={selectedIds.length}
                    onMove={showFolders ? this.openBulkMove : null}
                    onDelete={() => this.setState({pagesToBulkDelete: selectedPages})}
                    onClear={this.clearSelection}
                />

                <ConfirmDialog
                    open={!!this.state.pageToDelete}
                    title={this.state.pageToDelete ? `Delete "${this.state.pageToDelete.title}"?` : ''}
                    body="This page will be removed. Tickets already created in Jira stay in Jira."
                    confirmLabel="Delete page"
                    busy={this.state.deleteBusy}
                    onConfirm={this.confirmDelete}
                    onCancel={this.cancelDelete}
                />

                <ConfirmDialog
                    open={!!this.state.pagesToBulkDelete}
                    title={this.state.pagesToBulkDelete ? `Delete ${this.state.pagesToBulkDelete.length} pages?` : ''}
                    body="These pages will be removed. Tickets already created in Jira stay in Jira."
                    confirmLabel="Delete pages"
                    busy={this.state.deleteBusy}
                    onConfirm={this.confirmDelete}
                    onCancel={this.cancelDelete}
                />

                <RenameDialog
                    open={!!this.state.pageToRename}
                    initialValue={this.state.pageToRename?.title}
                    label="Page name"
                    busy={this.state.renameBusy}
                    onSave={this.performRename}
                    onCancel={() => this.setState({pageToRename: null})}
                />

                <MovePage
                    pageToMove={this.state.pageToMove}
                    folders={this.state.folders}
                    cancelAction={this.cancelMove}
                    performMoveAction={this.performMove}
                    destFolderId={this.state.destFolderId}
                    onFolderChangeAction={(e) => this.setState({destFolderId: e.target.value})}
                    errorMessage={this.state.movePageErrorMessage}
                    clearErrorAction={() => this.setState({movePageErrorMessage: null})}
                />

                {this.state.toast && (
                    <Toast
                        message={this.state.toast.message}
                        actionLabel={this.state.toast.actionLabel}
                        onAction={this.state.toast.onAction}
                        onDismiss={() => this.setState({toast: null})}
                    />
                )}
            </div>
        );
    }
}
